import {
  array,
  assert,
  func,
  literal,
  number,
  object,
  optional,
  string,
  define,
  union,
  type,
} from "superstruct";

type Schema = Record<string, any>;

function assertCustom(type: string, schema: Schema, payload: any) {
  try {
    assert(payload, object(schema));
  } catch (error: any) {
    throw new Error(
      `assertion error: invalid ${type} payload: ${error.message}`,
    );
  }
}

const component = union([
  array(
    object({
      name: string(),
      position: number(),
    }),
  ),
  define("Element", (value) => value instanceof Element),
]);

const searchLevel = union([literal("primary"), literal("secondary")]);

const language = union([
  literal("de"),
  literal("fr"),
  literal("it"),
  literal("en"),
]);

const userSchema = object({
  loginType: optional(string()),
  loginStatus: optional(union([literal(1), literal(0)])),
  language: optional(language),
  UUID: optional(string()),
  age: optional(string()),
  gender: optional(union([literal("f"), literal("m"), literal("n")])),
  zipcode: optional(string()),
});

const searchSchema = {
  value: string(),
  results: number(),
  level: searchLevel,
};

const pageLoaded = (payload: any) =>
  assertCustom(
    "pageLoaded",
    {
      pageInstanceID: string(),
      pageID: optional(string()),
      pageName: string(),
      destinationURI: string(),
      sysEnv: optional(string()),
      language,
      primaryCategory: string(),
      errorCode: optional(string()),
      errorMessage: optional(string()),
      user: optional(userSchema),
      search: optional(object(searchSchema)),
      referrer: optional(string()),
    },
    payload,
  );

const searchLoaded = (payload: any) =>
  assertCustom("searchLoaded", searchSchema, payload);

const genericSchema = {
  component,
  label: string(),
  location: optional(string()),
  variant: optional(string()),
  detail: optional(string()),
};

const accordion = (payload?: any) =>
  assertCustom(
    "accordion",
    {
      ...genericSchema,
      action: union([literal("open"), literal("close")]),
      headline: optional(string()),
    },
    payload,
  );

const genericNavigationSchema = {
  ...genericSchema,
  destination: string(),
  callback: optional(func()),
};

const genericNavigation = (payload: any) =>
  assertCustom("genericNavigation", genericNavigationSchema, payload);

const customNavigation = (payload: any) =>
  assertCustom(
    "customNavigation",
    {
      ...genericNavigationSchema,
      action: optional(string()),
      primaryCategory: optional(string()),
    },
    payload,
  );

const genericClick = (payload: any) =>
  assertCustom("genericClick", genericSchema, payload);

const teaserImpression = (payload: any) =>
  assertCustom(
    "teaserImpression",
    {
      ...genericSchema,
      destination: optional(string()),
      headline: string(),
    },
    payload,
  );

const teaserNavigation = (payload: any) =>
  assertCustom(
    "teaserNavigation",
    {
      ...genericNavigationSchema,
      headline: string(),
    },
    payload,
  );

const impression = (payload: any) =>
  assertCustom(
    "impression",
    {
      ...genericSchema,
      headline: optional(string()),
      type: string(),
    },
    payload,
  );

const download = (payload: any) =>
  assertCustom(
    "download",
    {
      ...genericNavigationSchema,
      filename: string(),
    },
    payload,
  );

const searchResult = (payload: any) =>
  assertCustom(
    "searchResult",
    {
      ...genericNavigationSchema,
      value: string(),
      position: number(),
      page: optional(number()),
      level: searchLevel,
      type: optional(union([literal("autosuggest"), literal("most searched")])),
    },
    payload,
  );

const loginType = (payload: any) =>
  assertCustom(
    "loginType",
    {
      loginType: string(),
    },
    payload,
  );

const customEvent = (payload: any) => {
  const customEventType = type({
    eventName: string(),
    primaryCategory: string(),
  });
  try {
    assert(payload, customEventType);
  } catch (error: any) {
    throw new Error(
      `assertion error: invalid customEvent payload: ${error.message}`,
    );
  }
};

export default {
  pageLoaded,
  searchLoaded,
  accordion,
  genericNavigation,
  customNavigation,
  genericClick,
  teaserImpression,
  teaserNavigation,
  impression,
  download,
  searchResult,
  loginType,
  customEvent,
};
